import { useState, useEffect, useCallback } from 'react'
import { apiClient } from '@/lib/api-client'
import type { AssetResponse, FrontendAsset } from '@/lib/api-types'
import { mapAssetResponseToFrontend } from '@/lib/api-types'
import { useToast } from '@/hooks/use-toast'

export interface UseAssetsResult {
  assets: FrontendAsset[]
  loading: boolean
  error: string | null
  uploadAsset: (file: File) => Promise<FrontendAsset | null>
  deleteAsset: (id: string) => Promise<void>
  refetch: () => Promise<void>
  page: number
  totalPages: number
  setPage: (page: number) => void
}

// ✅ Cache global de assets em localStorage
const ASSETS_CACHE_KEY = 'srt_assets_cache'
const ASSETS_CACHE_TTL = 5 * 60 * 1000 // 5 minutos

function getAssetsCache(): FrontendAsset[] | null {
  try {
    const cached = localStorage.getItem(ASSETS_CACHE_KEY)
    if (!cached) return null
    const { data, timestamp } = JSON.parse(cached)
    // Verifica se expirou
    if (Date.now() - timestamp > ASSETS_CACHE_TTL) {
      localStorage.removeItem(ASSETS_CACHE_KEY)
      return null
    }
    return data
  } catch {
    return null
  }
}

function setAssetsCache(assets: FrontendAsset[]): void {
  try {
    localStorage.setItem(ASSETS_CACHE_KEY, JSON.stringify({
      data: assets,
      timestamp: Date.now()
    }))
  } catch {
    // Ignorar erro de localStorage se não disponível
  }
}

export function useAssets(initialPage: number = 1, limit: number = 50): UseAssetsResult {
  const [assets, setAssets] = useState<FrontendAsset[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [page, setPage] = useState(initialPage)
  const [totalPages, setTotalPages] = useState(1)
  const { toast } = useToast()

  const fetchAssets = useCallback(async () => {
    try {
      // ✅ Verifica cache primeiro
      const cached = getAssetsCache()
      if (cached) {
        setAssets(cached)
        setLoading(false)
        return
      }

      setLoading(true)
      setError(null)
      const response = await apiClient.listAssets(page, limit)
      const backendAssets = response.data.assets
      const frontendAssets = backendAssets.map(mapAssetResponseToFrontend)
      setAssets(frontendAssets)
      // ✅ Salva em cache
      setAssetsCache(frontendAssets)
      setTotalPages(response.data.pages)
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Erro ao carregar assets'
      setError(msg)
      toast({
        title: 'Erro',
        description: msg,
        variant: 'destructive',
      })
    } finally {
      setLoading(false)
    }
  }, [page, limit, toast])

  useEffect(() => {
    fetchAssets()
  }, [fetchAssets])

  const uploadAsset = useCallback(
    async (file: File): Promise<FrontendAsset | null> => {
      try {
        // Validações locais
        const maxSize = 10485760 // 10MB
        if (file.size > maxSize) {
          const msg = `Arquivo muito grande. Máximo ${(maxSize / 1024 / 1024).toFixed(0)}MB`
          toast({
            title: 'Erro',
            description: msg,
            variant: 'destructive',
          })
          return null
        }

        if (!file.type.includes('png')) {
          const msg = 'Apenas arquivos PNG são permitidos'
          toast({
            title: 'Erro',
            description: msg,
            variant: 'destructive',
          })
          return null
        }

        const response = await apiClient.uploadAsset(file)
        const newAsset = response.data
        const frontendAsset = mapAssetResponseToFrontend(newAsset)
        
        // Adiciona à lista local
        setAssets((prev) => [frontendAsset, ...prev])
        // Atualiza cache
        setAssetsCache([frontendAsset, ...getAssetsCache() || []])
        
        toast({
          title: 'Sucesso',
          description: 'Asset enviado com sucesso',
        })
        
        return frontendAsset
      } catch (err: any) {
        const msg =
          err.response?.data?.detail ||
          err.message ||
          'Erro ao fazer upload'
        setError(msg)
        toast({
          title: 'Erro',
          description: msg,
          variant: 'destructive',
        })
        return null
      }
    },
    [toast]
  )

  const deleteAsset = useCallback(
    async (id: string): Promise<void> => {
      try {
        await apiClient.deleteAsset(id)
        setAssets((prev) => prev.filter((a) => a.id !== id))
        // Atualiza cache
        setAssetsCache(getAssetsCache()?.filter((a) => a.id !== id) || [])
        toast({
          title: 'Sucesso',
          description: 'Asset deletado com sucesso',
        })
      } catch (err: any) {
        const msg =
          err.response?.data?.detail ||
          err.message ||
          'Erro ao deletar asset'
        setError(msg)
        toast({
          title: 'Erro',
          description: msg,
          variant: 'destructive',
        })
        throw err
      }
    },
    [toast]
  )

  return {
    assets,
    loading,
    error,
    uploadAsset,
    deleteAsset,
    refetch: fetchAssets,
    page,
    totalPages,
    setPage,
  }
}


